import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import {
  LuMapPinned,
  LuPackageCheck,
  LuTruck
} from 'react-icons/lu';

const deliveryZones = [
  {
    title: 'Limited Area Delivery',
    icon: LuMapPinned,
    note:
      'We deliver within a limited area. The charge is Rs. 150 per km from our kitchen. Please contact us to confirm if your location is eligible for delivery.'
  },
  {
    title: 'Pickup Option',
    icon: LuPackageCheck,
    note:
      'If you are outside the delivery zone or prefer to save on delivery, you can pick up your cake directly from our kitchen studio. Pickup is available daily from 9:00 AM to 6:00 PM with prior notice.'
  }
];

const ShippingPolicy = () => (
  <>
    <Helmet>
      <title>Shipping & Delivery | Nithuja Cakes</title>
      <meta
        name="description"
        content="Learn about Nithuja Cakes’ limited area delivery, distance charges, and pickup options for your orders."
      />
    </Helmet>

    <div className="bg-gray-50 min-h-screen text-gray-800">
      <Navbar />

      <main className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-6 lg:px-8 xl:px-10">
        <p className="text-sm uppercase tracking-[0.35em] text-pink-600">Policies</p>

        <h1 className="mt-3 text-3xl sm:text-4xl font-serif font-bold text-gray-900">
          Shipping & Delivery
        </h1>

        <p className="mt-4 text-lg text-gray-600 leading-relaxed">
          Every cake is handmade with love and delivered carefully. Below you’ll find our delivery zones,
          distance-based charges, and handling instructions to ensure your cake arrives perfect.
        </p>

        <section className="mt-10 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-pink-50 text-pink-600">
              <LuTruck className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-pink-500">Care & Timing</p>
              <h2 className="mt-1 text-xl font-semibold text-gray-900">Fresh cakes, planned delivery</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Please place your order at least 5-7 days in advance. Larger or custom cakes may require 2-3 weeks.
                Rush orders are considered case by case and may include a small additional fee.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-8 grid gap-6">
          {deliveryZones.map(({ title, note, icon: Icon }, index) => (
            <section
              key={title}
              className={`rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7 ${index === 0 ? 'border-l-4 border-pink-500' : 'border-l-4 border-amber-400'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${index === 0 ? 'bg-pink-50 text-pink-600' : 'bg-amber-50 text-amber-600'}`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                  <p className="mt-3 text-gray-600 leading-relaxed">{note}</p>
                </div>
              </div>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
              <LuPackageCheck className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-emerald-800">Handling & Tips</h3>
              <ul className="mt-3 list-disc list-inside text-gray-600 space-y-2 leading-relaxed">
                <li>Keep cakes flat during transport and avoid direct sunlight.</li>
                <li>Refrigerate buttercream or cream-based cakes if serving more than 2 hours after delivery.</li>
                <li>If no one is available at the delivery address, we will wait 10 minutes and then coordinate a secondary delivery for a small additional fee.</li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
);

export default ShippingPolicy;
